import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Move } from "lucide-react";

const slides = [
    {
        id: "stone-patio",
        label: "Stone Patio",
        before: "https://customer-assets.emergentagent.com/job_agency-portfolio-70/artifacts/q9tw63vk_Before.png",
        after: "https://customer-assets.emergentagent.com/job_agency-portfolio-70/artifacts/vngixgx0_After.png",
    },
];

function Slider({ before, after }) {
    const [pos, setPos] = useState(50);
    const wrapRef = useRef(null);
    const dragging = useRef(false);

    const onMove = (clientX) => {
        if (!wrapRef.current) return;
        const rect = wrapRef.current.getBoundingClientRect();
        const x = ((clientX - rect.left) / rect.width) * 100;
        setPos(Math.max(0, Math.min(100, x)));
    };

    useEffect(() => {
        const up = () => (dragging.current = false);
        const move = (e) => {
            if (!dragging.current) return;
            onMove(e.touches ? e.touches[0].clientX : e.clientX);
        };
        window.addEventListener("mouseup", up);
        window.addEventListener("touchend", up);
        window.addEventListener("mousemove", move);
        window.addEventListener("touchmove", move);
        return () => {
            window.removeEventListener("mouseup", up);
            window.removeEventListener("touchend", up);
            window.removeEventListener("mousemove", move);
            window.removeEventListener("touchmove", move);
        };
    }, []);

    return (
        <div
            ref={wrapRef}
            className="relative w-full aspect-[16/10] overflow-hidden select-none rounded-sm border border-black/10 shadow-lg cursor-ew-resize"
            data-testid="before-after-slider"
            onMouseDown={(e) => {
                dragging.current = true;
                onMove(e.clientX);
            }}
            onTouchStart={(e) => {
                dragging.current = true;
                onMove(e.touches[0].clientX);
            }}
        >
            {/* After (base) */}
            <img
                src={after}
                alt="After"
                draggable={false}
                className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute top-4 right-4 bg-[#1E88E5] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-sm">
                After
            </div>

            {/* Before (clipped) */}
            <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: `${pos}%` }}
            >
                <img
                    src={before}
                    alt="Before"
                    draggable={false}
                    className="absolute inset-0 h-full object-cover"
                    style={{
                        width: wrapRef.current
                            ? `${wrapRef.current.offsetWidth}px`
                            : "100%",
                        maxWidth: "none",
                    }}
                />
                <div className="absolute top-4 left-4 bg-[#0a0a0a]/85 border border-white/20 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-sm">
                    Before
                </div>
            </div>

            {/* Handle */}
            <div
                className="absolute top-0 bottom-0 w-1 bg-white pointer-events-none shadow-[0_0_20px_rgba(255,255,255,0.6)]"
                style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
            >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-[#1E88E5] rounded-full flex items-center justify-center shadow-[0_0_0_4px_rgba(30,136,229,0.25)]">
                    <Move className="w-5 h-5 text-white rotate-90" />
                </div>
            </div>
        </div>
    );
}

export default function BeforeAfter() {
    const [active, setActive] = useState(0);

    return (
        <section
            id="results"
            data-testid="before-after-section"
            className="relative py-24 lg:py-32 bg-[#fafbfc] border-t border-black/5"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-[#1E88E5] font-bold mb-4">
                            / Real Transformations
                        </div>
                        <h2 className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-[#0a0a0a]">
                            Slide to see
                            <br />
                            <span className="text-[#1E88E5]">
                                the difference.
                            </span>
                        </h2>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {slides.map((s, i) => (
                            <button
                                key={s.id}
                                onClick={() => setActive(i)}
                                data-testid={`tab-${s.id}`}
                                className="px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-[#1E88E5] text-white"
                            >
                                {s.label} · Real Job
                            </button>
                        ))}
                    </div>
                </div>

                <motion.div
                    key={slides[active].id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <Slider
                        before={slides[active].before}
                        after={slides[active].after}
                    />
                </motion.div>

                <p className="mt-6 text-sm text-gray-500 text-center">
                    Drag the handle left or right — no filters, no edits, just
                    pressure and skill.
                </p>
            </div>
        </section>
    );
}
